import { AUDIO_CONTEXT_STATE, AUDIO_EFFECT_NAME, audioSourceNodes, isEnvSupport, } from './audio-helper';
import CompressorEffector from './effectors/compressor-effector';
import EqualizerEffector from './effectors/equalizer-effector';
import GainEffector from './effectors/gain-effector';
import ReverbEffector from './effectors/reverb-effector';
import SpatialStereoEffector from './effectors/spatial-stereo-effector';
/**
 * 声音控制模块，包括音效和音量增益等
 *
 * @export
 * @class AudioController
 * @typedef {AudioController}
 */
var AudioController = /** @class */ (function () {
    function AudioController(media, logger) {
        this.effectorList = [];
        this.currEffect = AUDIO_EFFECT_NAME.DEFAULT;
        this.currGain = 0;
        this.media = media;
        this.logger = logger;
    }
    AudioController.prototype.onPlay = function () {
        // 创建AudioContext并初步创建连接
        this.createContextAndConnect();
    };
    /**
    * 断开声音播放通路并销毁
    */
    AudioController.prototype.destroy = function () {
        this.effectorList.forEach(function (audioEffect) {
            audioEffect.destroy();
        });
        this.effectorList.splice(0, this.effectorList.length);
        if (this.audioSource) {
            this.audioSource.disconnect();
            this.audioSource = null;
        }
        // 关闭Context
        if (this.audioCtx) {
            if (this.audioCtx.state === AUDIO_CONTEXT_STATE.RUNNING) {
                this.audioCtx.suspend();
            }
            this.audioCtx = null;
        }
        this.gainEffector = null;
        this.spatialEffector = null;
        this.eqEffector = null;
        this.cpsEffector = null;
        this.rvbEffector = null;
        this.logger = null;
        this.currEffect = AUDIO_EFFECT_NAME.DEFAULT;
        this.currGain = 0;
    };
    /**
     * 设置增益百分比，范围[0,2]
     * 比如传入0.5，意味在当前音量大小下，增益50%，最大增益到200%，可能会出现破音
     * @param value 增益值
     */
    AudioController.prototype.setGainValue = function (value) {
        if (!isEnvSupport() || !this.isMediaSupport()) {
            this.logger.warn("setGainValue(), not supported, envi= ".concat(isEnvSupport(), " ,media= ").concat(this.isMediaSupport()));
            return;
        }
        if (!this.isContextRunning()) {
            this.logger.warn('setGainValue(), AudioContext is not running');
            return;
        }
        // 用于增益音效是否启用均衡器判断
        this.currGain = value;
        if (!this.gainEffector) {
            this.connectAudioPath();
        }
        this.gainEffector.setGainValue(value);
    };
    /**
     * 获取增益百分比，范围[0,2]，即0~200%
     */
    AudioController.prototype.getGainValue = function () {
        // 不直接返回currGain原因是：currGain并没有经过范围校验
        if (!this.gainEffector) {
            this.logger.warn('getGainValue(),gainEffector is null');
            return 0;
        }
        return this.gainEffector.getGainValue();
    };
    /**
     * 设置效果，目前同时只支持一种效果，暂时不支持多种效果混合，如果确实有需求后续迭代再支持
     * @param effectName 效果器名称
     */
    AudioController.prototype.setEffect = function (effectName) {
        if (!isEnvSupport() || !this.isMediaSupport()) {
            this.logger.warn("setEffect(), not supported, envi= ".concat(isEnvSupport(), " ,media= ").concat(this.isMediaSupport()));
            return;
        }
        if (!this.isContextRunning()) {
            this.logger.warn('setEffect(), AudioContext is not running');
            return;
        }
        if (this.currEffect === effectName) {
            // 不需要重复设置了
            this.logger.log("setEffect(), this.currEffect === effectName, effectName=".concat(effectName));
            return;
        }
        this.currEffect = effectName;
        this.connectAudioPath();
    };
    /**
     * 获取当前效果
     */
    AudioController.prototype.getEffect = function () {
        return this.currEffect;
    };
    /**
     * 使用播放器主循环调用此函数来做立体声环绕等效果
     */
    AudioController.prototype.loop = function () {
        var _a;
        if ((_a = this.spatialEffector) === null || _a === void 0 ? void 0 : _a.using) {
            this.spatialEffector.updateLoop();
        }
    };
    /**
     * 创建AudioContext并初步创建连接
     * 如果audioSourceNodes里有AudioContext和AudioSourceNode，需要优先复用
     */
    AudioController.prototype.createContextAndConnect = function () {
        var _this = this;
        var _a;
        if (!isEnvSupport() || !this.isMediaSupport()) {
            // 如果不支持就不执行下去了
            return;
        }
        if (audioSourceNodes.has(this.media)) {
            // 此处需要注意：
            // 使用audioSourceNodes存储的原因是，一旦audioSource接管了video元素的声音，就必须使用该audioSoure才能播放出声音，否则出现无法播放卡主的问题
            // 所以在BaseVideoNode被销毁时或者AudioContext被关闭，需要video元素复用的情况时，只能找回该对应audioSoure才能正常播放
            this.audioSource = audioSourceNodes.get(this.media);
            this.audioCtx = this.audioSource.context;
            if (((_a = this.audioCtx) === null || _a === void 0 ? void 0 : _a.state) === AUDIO_CONTEXT_STATE.SUSPENDED) {
                // 复用audioSource需要创建声音连接，否则会没有声音
                this.audioCtx.resume().then(function () { return _this.connectAudioPath(); });
            }
        }
        else {
            // 如果没有，就创建新的AudioContext和AudioSourceNode
            this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            this.audioSource = this.audioCtx.createMediaElementSource(this.media);
            audioSourceNodes.set(this.media, this.audioSource);
            // audioSource接管声音后，需要创建声音连接，否则会没有声音
            this.connectAudioPath();
        }
    };
    /**
     * 创建声音通路
     */
    AudioController.prototype.connectAudioPath = function () {
        // 如果效果数组为空，需要重新初始化
        if (this.effectorList.length === 0) {
            this.createAudioEffectors();
        }
        // 先断开上次已经连接上的设备
        this.disconnect();
        // 激活对应效果器
        this.activeEffects();
        // 更新连接效果器
        this.reconnect();
    };
    /**
     * 创建audioContext,audioSource和各个效果器
     */
    AudioController.prototype.createAudioEffectors = function () {
        this.logger.log('audioController createAudioEffectors()');
        // 音量增益效果器
        this.gainEffector = new GainEffector(this.audioCtx, this.logger);
        this.effectorList.push(this.gainEffector);
        // 立体声效果器
        this.spatialEffector = new SpatialStereoEffector(this.audioCtx, this.logger);
        this.effectorList.push(this.spatialEffector);
        // EQ均衡效果器
        this.eqEffector = new EqualizerEffector(this.audioCtx, this.logger);
        this.effectorList.push(this.eqEffector);
        // 压缩效果器
        this.cpsEffector = new CompressorEffector(this.audioCtx, this.logger);
        this.effectorList.push(this.cpsEffector);
        // 混响效果器
        this.rvbEffector = new ReverbEffector(this.audioCtx, this.logger);
        this.effectorList.push(this.rvbEffector);
    };
    /**
     * 需要media是否为HTMLMediaElement和是否允许跨域判断
     * WASM使用Canvas实现的FakeHTMLMediaElement是不支持音效
     * 不支持跨域则会出现WebAudio劫持Video后无法出声音情况
     */
    AudioController.prototype.isMediaSupport = function () {
        // 如果media不是真实的video元素，不支持
        if (!this.media || !(this.media instanceof HTMLMediaElement)) {
            return false;
        }
        // 如果视频文件没有设置跨域，会出现WebAudio劫持Video后无法出声音情况，浏览器会报如下错误：
        // MediaElementAudioSource outputs zeroes due to CORS access restrictions for https://xxx.mp4
        // 需要给SuperPlayer加上设置跨域
        // videoElementConfig: {
        //     crossorigin: true,
        // }
        if (this.media.getAttribute('crossorigin') !== 'anonymous') {
            return false;
        }
        return true;
    };
    /**
     * 判断audioCtx是否为空或者被关闭
     */
    AudioController.prototype.isContextRunning = function () {
        return this.audioCtx && this.audioCtx.state === 'running';
    };
    /**
    * 建立声音播放通路
    */
    AudioController.prototype.reconnect = function () {
        var _this = this;
        // audioSource（音源) -> 各个音效节点 ->destination(输出目标)
        var currNode = this.audioSource;
        var nodesName = 'audioSource -> ';
        this.effectorList.forEach(function (audioEffect) {
            if (audioEffect.using) {
                currNode = audioEffect.connectBy(currNode);
                audioEffect.setPreConfigName(_this.currEffect);
                nodesName += "".concat(audioEffect.getEffectorName(), " -> ");
            }
        });
        currNode.connect(this.audioCtx.destination);
        this.logger.log("audioController reconnect(), using audioEffect = ".concat(nodesName, " destination"));
    };
    /**
     * 断开声音播放通路
     */
    AudioController.prototype.disconnect = function () {
        this.audioSource.disconnect();
        this.effectorList.forEach(function (audioEffect) {
            if (audioEffect.using) {
                audioEffect.disconnect();
                audioEffect.using = false;
            }
        });
        this.logger.log('audioController disconnect()');
    };
    /**
     * 按currEffect激活对应效果器
     */
    AudioController.prototype.activeEffects = function () {
        // 增益效果器衡起用
        this.gainEffector.using = true;
        // 压缩效果器在有音效时或者默认音效有增益都需要起用，以防止破音
        this.cpsEffector.using = true;
        switch (this.currEffect) {
            case AUDIO_EFFECT_NAME.SPATIAL_STEREO:
                // SPATIAL_STEREO使用立体声效果器
                this.spatialEffector.using = true;
                break;
            case AUDIO_EFFECT_NAME.CONCERT_HALL:
            case AUDIO_EFFECT_NAME.LIVING_ROOM:
            case AUDIO_EFFECT_NAME.LIVE:
                // CONCERT_HALL和LIVE_HOUSE、LIVE属于混响效果器
                this.rvbEffector.using = true;
                break;
            case AUDIO_EFFECT_NAME.DEFAULT:
                // 选择默认效果并且无增益时，则去掉压缩器效果恢复原有音效
                if (this.currGain === 0) {
                    this.cpsEffector.using = false;
                }
                break;
            default:
                // 其余都是EQ均衡效果器
                this.eqEffector.using = true;
                break;
        }
    };
    return AudioController;
}());
export { AudioController };
